import React, { useEffect, useState } from 'react'
import { Icon, InputNumber, Modal, Popover, Select } from 'antd'
import { AiScore } from './AiBasis'

/**
 * AiLinkPanel — the "AI link" control on the PV tab, and the run's progress.
 *
 * Start opens a dialog that states what will happen and asks for the defaults for
 * items that become NEW assets - Owned by and Invoice value, both required (no
 * pre-filled value; 0 only if typed) - then the container runs the AI search item by item (see runAiLink in
 * index.js). This panel shows: progress (n of N, elapsed), the item being checked,
 * counts (linked / new / failed / skipped), Stop, and a table of every item's
 * outcome with its reason. Nothing is hidden: a failed item is listed with its
 * error and left undecided.
 *
 * AiTag — the small "AI" tag on a PV row whose current action is the one the AI set.
 * Design: docs/reconciliation-cockpit-ai-link-mode.md
 */

const REASON = {
  MATCH: 'AI match',
  NO_OPTIONS: 'no linkable option',
  ALL_CHECKED_BELOW: 'all options checked, none ≥ threshold',
  CAP_REACHED_BELOW: 'none ≥ threshold within the AI check cap',
}
const pct = (p) => `${Math.round(p * 100)}%`

const Elapsed = ({ since }) => {
  const [, tick] = useState(0)
  useEffect(() => {
    const t = setInterval(() => tick(n => n + 1), 1000)
    return () => clearInterval(t)
  }, [])
  const s = Math.max(0, Math.round((Date.now() - since) / 1000))
  return <span className="tnum">{s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`}</span>
}

// The AI tag on a PV row. A link shows the score; clicking it shows what the AI used
// (fetched once by comparison id). A new asset shows why no link was chosen.
export const AiTag = ({ ai }) => {
  if (!ai) return null
  if (ai.action === 'LINK_ASSET' && typeof ai.sameProbability === 'number') {
    return <AiScore prob={ai.sameProbability} assetCode={ai.assetCode} comparisonId={ai.comparison} className="ai-tag" />
  }
  const content = (
    <div style={{ fontSize: 12, maxWidth: 360 }}>
      <b>New asset — set by AI link</b> ({new Date(ai.createdAt).toLocaleString()})<br />
      {REASON[ai.reason] || ai.reason}<br />
      Options checked {ai.optionsChecked} of {ai.optionsTotal} · new AI checks {ai.jevCalls} (cap {ai.jevCallCap})
      {ai.defaultsApplied && ai.defaultsApplied.length ? <><br />Run defaults applied: {ai.defaultsApplied.map(f => f === 'ownedBy' ? 'owner' : 'invoice value').join(', ')}</> : null}
    </div>
  )
  return (
    <Popover content={content} trigger="click" placement="left">
      <span className="ai-tag" onClick={(e) => e.stopPropagation()}>AI new</span>
    </Popover>
  )
}

const AiLinkPanel = ({ run, eligible, shown, threshold, cap, ownerOptions = [], lastOwner, onStart, onStop, onClose, backlog, onBacklog, onBacklogStop, onBacklogClose }) => {
  const [showItems, setShowItems] = useState(false)
  const [asking, setAsking] = useState(false)
  const [owner, setOwner] = useState(undefined)
  const [rate, setRate] = useState(null)
  const running = run && run.status === 'running'
  const ownerOk = !!owner && ownerOptions.some(o => String(o._id) === String(owner))
  const rateOk = typeof rate === 'number' && isFinite(rate) && rate >= 0
  const open = () => {
    // Convenience only: pre-select the owner last used for new assets, if it still exists.
    setOwner(lastOwner && ownerOptions.some(o => String(o._id) === String(lastOwner)) ? lastOwner : undefined)
    setRate(null)
    setAsking(true)
  }
  const start = () => { setAsking(false); onStart({ ownedBy: owner, rate }) }

  const dialog = (
    <Modal visible={asking} onCancel={() => setAsking(false)} width={580}
      title={`AI link ${eligible} PV item${eligible === 1 ? '' : 's'}`}
      okText="Start AI link" onOk={start} okButtonProps={{ disabled: !ownerOk || !rateOk }}>
      <div style={{ fontSize: 13 }}>
        <p>For each PV item in the current filter that has <b>no decision yet</b>
          {shown - eligible > 0 ? ` (${shown - eligible} already decided or processed are left alone)` : ''}, the link
          options are checked with the AI in priority order:</p>
        <ul style={{ paddingLeft: 18 }}>
          <li>first option at <b>≥ {pct(threshold)}</b> → <b>Link</b> (also when it overwrites the asset's model)</li>
          <li>none at {pct(threshold)} within <b>{cap} new AI checks</b>, all checked, or no option → <b>New asset</b></li>
        </ul>
        <div style={{ border: '1px solid #e0e6ee', borderRadius: 8, padding: '10px 12px', margin: '10px 0' }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Defaults for items marked New asset (required)</div>
          <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 8, alignItems: 'center' }}>
            <span>Owned by</span>
            <Select showSearch optionFilterProp="children" value={owner} placeholder="Select owner"
              onChange={setOwner} style={{ width: '100%' }}>
              {ownerOptions.map(o => <Select.Option key={o._id} value={o._id}>{o.name}</Select.Option>)}
            </Select>
            <span>Invoice value</span>
            <InputNumber min={0} value={rate} placeholder="per asset" style={{ width: '100%' }}
              onChange={(v) => setRate(typeof v === 'number' ? v : null)} />
          </div>
          <div style={{ color: '#8695a7', fontSize: 12, marginTop: 6 }}>
            Applied only where the item has no owner / value yet (a value from the scan or one already entered is kept);
            shown as "AI link default" in the item's details, editable before Save.
          </div>
          {!ownerOptions.length && <div style={{ color: '#f5222d', marginTop: 6 }}>No owners are loaded — AI link cannot start.</div>}
        </div>
        <p>Decisions are set <b>pending save</b> — review them, then Save as usual. Up to {cap} AI checks per item
          (≈ 1–3 s each); a large filter can take a long time. You can Stop at any time.</p>
      </div>
    </Modal>
  )

  return (
    <div className="ai-bar">
      <div className="jv-row">
        {!running && (
          <button type="button" className="ai-btn" disabled={!eligible} onClick={open}
            title={eligible ? '' : 'Every PV item in this filter already has a decision'}>
            <Icon type="thunderbolt" /> AI link {eligible ? `(${eligible})` : ''}
          </button>
        )}
        {run && (
          <>
            {running
              ? <span className="jv-run"><Icon type="loading" /> AI link: item {Math.min(run.done + 1, run.total)} of {run.total}
                {run.current ? <> — <b>{run.current.code}</b></> : null} · <Elapsed since={run.startedAt} /></span>
              : <span className={run.status === 'done' ? '' : 'jv-err'}>
                AI link {run.status === 'done' ? 'finished' : run.status === 'halted' ? 'halted' : 'stopped'}: {run.done} of {run.total} items
                {run.haltReason ? ` — ${run.haltReason}` : ''}</span>}
            <span className="jv-meta">
              linked <b>{run.linked}</b> · new <b>{run.newCount}</b> · failed <b>{run.errors}</b>
              {run.skipped ? <> · skipped <b>{run.skipped}</b></> : null}
            </span>
            {running && <a onClick={onStop}>Stop</a>}
            {run.items.length > 0 && <a className="jv-toggle" onClick={() => setShowItems(v => !v)}>{showItems ? 'Hide' : 'Show'} items</a>}
            {!running && <a className="jv-toggle" onClick={onClose}>Dismiss</a>}
            {!running && <span className="jv-meta">Decisions are pending save.</span>}
          </>
        )}
      </div>
      {dialog}
      <div className="jv-row" style={{ marginTop: 4 }}>
        {(!backlog || backlog.status !== 'running') && (
          <a className="jv-toggle" onClick={onBacklog}
            title="Describe, once, every photo AI link needs (PV photos of this voucher, then register assets' latest photos). Already-described photos are skipped.">
            Prepare photos (one-time)
          </a>
        )}
        {backlog && (
          <>
            {backlog.status === 'running'
              ? <span className="jv-run"><Icon type="loading" /> Describing photos — {backlog.phase === 'PV' ? 'PV items' : 'register assets'} · <Elapsed since={backlog.startedAt} /></span>
              : <span className={backlog.status === 'done' ? '' : 'jv-err'}>Photo preparation {backlog.status}{backlog.error ? ` — ${backlog.error}` : ''}</span>}
            {['PV', 'REGISTER'].filter(ph => backlog.byPhase[ph]).map(ph => {
              const a = backlog.byPhase[ph]
              return (
                <span key={ph} className="jv-meta">
                  {ph === 'PV' ? 'PV' : 'Register'}: {a.processed}{a.total != null ? ` / ${a.total}` : ''} {ph === 'PV' ? 'PVs' : 'assets'} ·
                  {' '}{a.described} described · {a.already} already · {a.failed} failed{a.notDescribed ? ` · ${a.notDescribed} not described` : ''}
                </span>
              )
            })}
            {backlog.status === 'running' ? <a onClick={onBacklogStop}>Stop</a> : <a className="jv-toggle" onClick={onBacklogClose}>Dismiss</a>}
          </>
        )}
      </div>
      {run && showItems && (
        <table className="jv-list">
          <thead><tr><th>Item</th><th>Outcome</th><th>Reason</th><th>Options checked</th><th>New AI checks</th></tr></thead>
          <tbody>
            {run.items.map((it, i) => (
              <tr key={it.uid + i}>
                <td><b>{it.code}</b></td>
                <td>{it.outcome === 'linked'
                  ? <>Link → <b>{it.assetCode}</b> {typeof it.score === 'number' ? pct(it.score) : ''}{it.overwrite ? ' · overwrites model' : ''}</>
                  : it.outcome === 'new' ? <>New asset{it.defaultsApplied && it.defaultsApplied.length
                    ? <span className="jv-meta"> · defaults set: {it.defaultsApplied.map(f => f === 'ownedBy' ? 'owner' : 'value').join(', ')}</span>
                    : <span className="jv-meta"> · kept its own owner / value</span>}</>
                    : it.outcome === 'error' ? <span className="jv-err">failed — left undecided</span> : 'skipped'}
                  {it.auditError && <div className="jv-err">AI record not saved: {it.auditError}</div>}</td>
                <td>{it.outcome === 'error' || it.outcome === 'skipped' ? it.note : (REASON[it.reason] || it.reason)}</td>
                <td className="tnum">{it.optionsChecked != null ? `${it.optionsChecked} / ${it.optionsTotal}` : '—'}</td>
                <td className="tnum">{it.jevCalls != null ? it.jevCalls : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

export default AiLinkPanel
